import { z } from "zod";
import type { SupplierDocumentType } from "@fiscalo/shared-types";

export const SUPPLIER_DOCUMENT_TYPES: SupplierDocumentType[] = ["RUT", "CI", "EXT"];
export const SUPPLIER_CURRENCY_OPTIONS = ["UYU", "USD"] as const;

/**
 * Curated subset of countries Fiscalo's users invoice most often. Same
 * list as ClientForm for consistency; falls back to "OT" (Otro) when the
 * supplier is foreign but the country isn't on the short list.
 */
export const SUPPLIER_COUNTRY_OPTIONS = [
  { value: "UY", label: "Uruguay" },
  { value: "AR", label: "Argentina" },
  { value: "BR", label: "Brasil" },
  { value: "CL", label: "Chile" },
  { value: "PY", label: "Paraguay" },
  { value: "BO", label: "Bolivia" },
  { value: "PE", label: "Perú" },
  { value: "EC", label: "Ecuador" },
  { value: "CO", label: "Colombia" },
  { value: "VE", label: "Venezuela" },
  { value: "MX", label: "México" },
  { value: "US", label: "Estados Unidos" },
  { value: "CA", label: "Canadá" },
  { value: "ES", label: "España" },
  { value: "OT", label: "Otro" },
] as const;

export const supplierFormSchema = z
  .object({
    legalName: z.string().trim().min(1, "Razón social requerida").max(255),
    displayName: z.string().trim().max(255).optional().or(z.literal("")),
    documentType: z.enum(["RUT", "CI", "EXT"]),
    rut: z.string().trim().max(32).optional().or(z.literal("")),
    countryCode: z.string().trim().max(2).optional().or(z.literal("")),
    docRecepExt: z.string().trim().max(20).optional().or(z.literal("")),
    address: z.string().trim().max(500).optional().or(z.literal("")),
    ciudad: z.string().trim().max(30).optional().or(z.literal("")),
    departamento: z.string().trim().max(30).optional().or(z.literal("")),
    email: z
      .string()
      .trim()
      .max(255)
      .optional()
      .or(z.literal(""))
      .refine(
        (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
        "Email inválido",
      ),
    phone: z.string().trim().max(64).optional().or(z.literal("")),
    defaultCurrency: z.enum(["UYU", "USD"]).optional(),
    defaultCategory: z.string().trim().max(64).optional().or(z.literal("")),
    autoAckCfeOnReceive: z.boolean(),
    notes: z.string().trim().max(2000).optional().or(z.literal("")),
  })
  .superRefine((val, ctx) => {
    if (val.documentType === "RUT") {
      const digits = val.rut?.replace(/\D/g, "") ?? "";
      if (digits.length !== 12) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "RUT debe tener 12 dígitos",
          path: ["rut"],
        });
      }
    } else if (val.documentType === "CI") {
      const digits = val.rut?.replace(/\D/g, "") ?? "";
      if (digits.length < 7 || digits.length > 8) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "CI debe tener 7 u 8 dígitos",
          path: ["rut"],
        });
      }
    } else if (val.documentType === "EXT") {
      if (!val.countryCode?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "País requerido para proveedor extranjero",
          path: ["countryCode"],
        });
      }
    }
  });

export type SupplierFormValues = z.infer<typeof supplierFormSchema>;

export const defaultSupplierFormValues: SupplierFormValues = {
  legalName: "",
  displayName: "",
  documentType: "RUT",
  rut: "",
  countryCode: "UY",
  docRecepExt: "",
  address: "",
  ciudad: "",
  departamento: "",
  email: "",
  phone: "",
  defaultCurrency: undefined,
  defaultCategory: "",
  autoAckCfeOnReceive: false,
  notes: "",
};
